import { EXCLUDED_SECTOR_IDS } from '@/finsight/application/actions/deals/constants';
import * as FILTER_TYPES from '@/finsight/application/actions/pageFilters/constants';
import { IFilters, IFiltersPayload, ITopParentsPayload } from '@/finsight/application/TopCompanies/interfaces';
import { getFiltersByPairs } from '@/finsight/application/actions/profile/helpers/getFiltersByPairs';
import { generateFileName } from '@/finsight/application/actions/company/helpers';

export const getFilters = ({
  profileType,
  isConsumerOverview,
  productIds,
  regionIds,
  industry,
  sector,
  includedParentId,
  sectorsList,
  industriesList,
  mapping,
  leagueType,
  ...additionalFilters
}: IFilters) => {
  let filters = {
    productIds,
    regionIds,
    sector: null,
    industry: null,
    includedParentId: null,
    isOverview: (!industry && !sector && !leagueType),
    isConsumerOverview,
  };

  if (sector) {
    filters.sector = sectorsList[sector];
  }

  if (industry) {
    filters.industry = industriesList[industry];
  }

  if (includedParentId) {
    filters.includedParentId = includedParentId;
  }

  const profileFilters = getFiltersByPairs({
    mapping,
    profileType,
    selectedProducts: filters.productIds,
    selectedRegions: filters.regionIds,
  });

  // @todo rewrite getTopCompaniesBy to enum type
  const sponsorFilters = includedParentId ? {
    getDealsBy: FILTER_TYPES.COMPANY,
    getTopCompaniesBy: FILTER_TYPES.PARENT,
    getChartBy: FILTER_TYPES.PARENT,
    targetViewIds: includedParentId,
    includedCompanyRole: FILTER_TYPES.PARENT,
  } : {};

  const scorecardFilters = leagueType ? {
    getDealsBy: FILTER_TYPES.COMPANY,
  } : {};

  return {
    profileType,
    hasEitherPricingOrAnnouncedDate: true,
    ...filters,
    ...profileFilters,
    ...sponsorFilters,
    ...scorecardFilters,
    ...additionalFilters,
  };
};

const preparePagePayload = (pageFilters, payload) => {
  let filters: Partial<IFiltersPayload> = {};

  if (
      pageFilters.isOverview
      && pageFilters.isConsumerOverview
      && pageFilters.getTopCompaniesBy === FILTER_TYPES.SECTOR
  ) {
    filters.excludedSectorIds = EXCLUDED_SECTOR_IDS;
  }

  if (pageFilters.includedParentId) {
    filters.includedParentId = pageFilters.includedParentId;
  }

  if (pageFilters.sector) {
    filters.includedSectorId = pageFilters.sector.sectorId;

    if (pageFilters.sector.subsectorId) {
      filters.includedSubsectorId = pageFilters.sector.subsectorId;
    }
  }

  if (pageFilters.industry) {
    filters.includedIndustryId = pageFilters.industry.industryId;

    if (pageFilters.industry.subindustryId) {
      filters.includedSubindustryId = pageFilters.industry.subindustryId;
    }
  }

  return { ...payload, filters: { ...payload.filters, ...filters } };
};

export const prepareTopPeriodsPayload = (pageFilters) => {
  let payload = {
    groupBy: pageFilters.getTopCompaniesBy,
    filters: {
      includedProductIds: pageFilters.productIds,
      includedRegionIds: pageFilters.regionIds,
    },
  };

  return preparePagePayload(pageFilters, payload);
};

export const prepareTopCompanyPayload = ({ pageFilters, perPage, period, dateFrom, dateTo, includedCompanyRole }) => {
  let payload: Partial<ITopParentsPayload> = {
    perPage,
    groupBy: pageFilters.getTopCompaniesBy,
    period,
    filters: {
      includedProductIds: pageFilters.productIds,
      includedRegionIds: pageFilters.regionIds,
      includedCompanyRole,
      dateFrom,
      dateTo,
    },
  };

  return preparePagePayload(pageFilters, payload);
};

export const prepareExportToExcelPayload = ({ pageFilters, selectedPeriod, products, regions, parentName }) => {
  const fileName = generateFileName({
    productIds: pageFilters.productIds,
    products,
    regionIds: pageFilters.regionIds,
    regions,
    period: selectedPeriod.label,
    sectorName: pageFilters.sector ? pageFilters.sector.metaTitleName : '',
    industryName: pageFilters.industry ? pageFilters.industry.metaTitleName : '',
    parentName,
  });

  let payload: Partial<ITopParentsPayload> = {
    groupBy: pageFilters.getTopCompaniesBy,
    period: selectedPeriod.value,
    fileName,
    filters: {
      excludedSectorIds: [],
      includedProductIds: pageFilters.productIds,
      includedRegionIds: pageFilters.regionIds,
      dateFrom: selectedPeriod.dateFrom,
      dateTo: selectedPeriod.dateTo,
    },
  };

  return preparePagePayload(pageFilters, payload);
};
